import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import ContasForm from '../ContasForm';
import api3005 from '../../services/api3005';

/* ===== ESTILOS ===== */

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 20px;
  font-family: 'Arial', sans-serif;
  color: #333;
  text-align: center;
`;

const Wrapper = styled.div`
  overflow-x: auto;
  margin-top: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Thead = styled.thead`
  background-color: #151e3d;
  color: white;
`;

const Th = styled.th`
  padding: 10px;
  text-align: left;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #d3d3d3;
`;

const Td = styled.td`
  padding: 10px;
`;

const Button = styled.button`
  margin: 2px;
  padding: ${({$smal}) => ($smal ? "3px 6px" : "6px 12px")};
  font-size: ${({$smal}) => ($smal ? "11px" : "14px")};
  cursor: pointer;
  border: 1px solid #007bff;
  border-radius: 6px;
  color: #007bff;
  background-color: transparent;
  white-space: nowrap;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #007bff;
    color: white;
  }

  &:active {
    transform: scale(0.98);
    background-color: #0056b3;
  }
`;

const EditButton = styled(Button)`
  border-color: #007bff;
  color: #007bff;

  &:hover {
    background-color: #007bff;
    color: white;
  }
`;

const StatusButton = styled(Button)`
  min-width: 150px;

  border-color: ${(props) =>
    props.status === 'PAGO' ? '#28a745' : 'red'};

  color: ${(props) =>
    props.status === 'PAGO' ? '#28a745' : 'red'};

  &:hover {
    background-color: ${(props) =>
      props.status === 'PAGO' ? '#28a745' : 'red'};
    color: white;
  }

  &:active {
    background-color: ${(props) =>
      props.status === 'PAGO' ? '#1e7e34' : 'darkred'};
    border-color: ${(props) =>
      props.status === 'PAGO' ? '#1e7e34' : 'darkred'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const FilterInput = styled.input`
  padding: 0.6rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  min-width: 250px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

/* ===== COMPONENTE ===== */

const ContasList = () => {
  const [contas, setContas] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [contaEditando, setContaEditando] = useState(null);
  const [filtroNome, setFiltroNome] = useState('');
  const [error, setError] = useState(null);

  const fetchContas = async () => {

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    if (role !== 'admin') {
      setError('Você precisa ser admin para acessar esta página.');
      return;
    }

    try {
      const response = await api3005.get('/api/contas-pagar', {
        headers: { Authorization: `Bearer ${token}` },
      });

      setContas(response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'Erro ao buscar contas');
    }
  };

  useEffect(() => {
    fetchContas();
  }, []);

  const toggleStatus = async (conta) => {
    const novoStatus = conta.status === 'PAGO' ? 'PENDENTE' : 'PAGO';

    const result = await Swal.fire({
      title: `Deseja marcar como ${novoStatus}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');

      await api3005.put(
        `/api/contas-pagar/${conta.id}/status`,
        {
          status: novoStatus
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      fetchContas();
    } catch (error) {
      setError(error.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const abrirEdicao = (conta) => {
    setContaEditando(conta);
    setShowEditModal(true);
  };

  const listaFiltrada = contas.filter((c) =>
    filtroNome
      ? c.nome?.toLowerCase().includes(filtroNome.toLowerCase())
      : true
  );

  return (
    <div>
      <Title>Contas a Pagar</Title>

      <Centered>
        <Button onClick={() => setShowCreateModal(true)}>
          Criar Conta
        </Button>

        <FilterInput
          placeholder="Filtrar por nome"
          value={filtroNome}
          onChange={(e) => setFiltroNome(e.target.value)}
        />
      </Centered>

      {error && (
        <p style={{ color: 'red', textAlign: 'center' }}>
          {error}
        </p>
      )}

      <Wrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Nome</Th>
              <Th>Categoria</Th>
              <Th>Valor</Th>
              <Th>Parcela</Th>
              <Th>Vencimento</Th>
              <Th>Ações</Th>
            </tr>
          </Thead>
          <tbody>
            {listaFiltrada.map((conta) => (
              <Tr key={conta.id}>
                <Td>{conta.nome}</Td>
                <Td>{conta.categoria}</Td>
                <Td>R$ {Number(conta.valor).toFixed(2)}</Td>
                <Td>
                  {conta.numero_parcela}/{conta.total_parcelas}
                </Td>
                <Td>
                  {new Date(conta.data_vencimento).toLocaleDateString('pt-BR')}
                </Td>
                <Td>
                  <EditButton onClick={() => abrirEdicao(conta)}>
                    Editar
                  </EditButton>

                    <StatusButton
                        status={conta.status}
                        onClick={() => toggleStatus(conta)}
                    >
                        {conta.status === 'PAGO' ? 'PAGO' : 'PENDENTE'}
                    </StatusButton>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Wrapper>

      {showCreateModal && (
        <ModalOverlay>
          <ContasForm
            modo="criar"
            onClose={() => setShowCreateModal(false)}
            onCreated={fetchContas}
          />
        </ModalOverlay>
      )}

      {showEditModal && contaEditando && (
        <ModalOverlay>
          <ContasForm
            modo="editar"
            conta={contaEditando}
            onClose={() => setShowEditModal(false)}
            onCreated={fetchContas}
          />
        </ModalOverlay>
      )}
    </div>
  );
};

export default ContasList;